import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { EntityType } from "@models/common/entity-type.enum";
import { Relationship } from '@models/relationships/relationship';
import { SecurityHolding } from '@models/shares/security-holding';
import { Trust } from "@models/trusts/trust";
import { jurisdictionOptions, JurisdictionType, TrustSubType } from "@models/trusts/trusts-enums";
import { FileCreateDto } from '@models/ui-models/files';
import {
  NgbAccordionBody,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbAccordionToggle,
  NgbActiveModal,
  NgbDateStruct,
  NgbModal,
  NgbTooltip
} from "@ng-bootstrap/ng-bootstrap";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import {
  IFileUploadConfig,
  UploadFilesModalComponent
} from '@shared/components/documents-related/annual-upload-files-modal/upload-files-modal.component';
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import {
  AbnMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/abn-masked-input/abn-masked-input.component";
import {
  MultipleInputDatepickerComponent
} from "@shared/controls/multiple-input-datepicker/multiple-input-datepicker.component";
import { SelectGroupsComponent, SelectOptionGroup } from "@shared/controls/select-groups/select-groups.component";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { enumToSteps } from "@shared/utils/enums-to-list-formatter";
import { toBase64 } from '@shared/utils/to-base64';
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { ToastrService } from "ngx-toastr";
import { finalize, take } from "rxjs";
import {
  IntegrationsEntitySearchControlComponent
} from "../../../controls/integrations-entity-search-control/integrations-entity-search-control.component";
import {
  AddressFormGroupComponent
} from "../../../reusable-form-groups/address-form-group/address-form-group.component";
import { StepperFormComponent } from "../../stepper-form";
import { analyseTrustPdfMessages } from "../constants/analyse-trust-pdf-messages";
import { AddTrustSteps, getTrustType, trustTypesOptionGroups } from "./form-add-trust.model";

export const commonFileUploadConfig: IFileUploadConfig = {
  allowedMaxFileSizeMB: 10,
  allowedMaxFilesSummarySizeMB: 10,
  maxFilesNumber: 1,
  acceptedFormatsList: ['.pdf'],
};

@Component({
  selector: 'app-form-add-trust',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    IntegrationsEntitySearchControlComponent,
    SelectGroupsComponent,
    AbnMaskedInputComponent,
    DatePickerComponent,
    FlagsAutocompleteComponent,
    DisclaimerComponent,
    AddressFormGroupComponent,
    MultipleInputDatepickerComponent,
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionToggle,
    NgbAccordionCollapse,
    NgbAccordionBody,
    NgbTooltip,
  ],
  templateUrl: './form-add-trust.component.html',
  styleUrls: [
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
    'form-add-trust.component.scss'
  ],
})
export class FormAddTrustComponent implements OnInit {
  @ViewChild(NgbAccordionDirective) additionalDetailsAccordion!: NgbAccordionDirective;

  private modalService = inject(NgbModal);
  private activeModal = inject(NgbActiveModal);
  private trustsApiService = inject(TrustsApiService);
  private toastr = inject(ToastrService);

  readonly StepsEnum = AddTrustSteps;
  readonly steps = enumToSteps<AddTrustSteps>(AddTrustSteps);
  readonly trustTypesOptionGroups: SelectOptionGroup[] = trustTypesOptionGroups;
  readonly jurisdictionOptions = jurisdictionOptions;
  readonly EntityType = EntityType;
  readonly analyseTrustPdfMessages = analyseTrustPdfMessages;

  minDate: NgbDateStruct = DatepickerHelper.getStructFromDate(new Date(1, 0, 1920)); // Extend date range from 1920 January
  maxDate: NgbDateStruct = DatepickerHelper.getNextNYearsStruct(100);

  config: IFileUploadConfig = commonFileUploadConfig;
  currentStep = AddTrustSteps.TrustInformation;
  currentStepIndex = 0;
  isLoading = false;
  trustFromDocument: Trust | null = null;

  stepperForm = new FormGroup({
    [AddTrustSteps.TrustInformation]: new FormGroup({
      name: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.maxLength(50)]),
      abn: new FormControl<string | null>(null),
      trustSubType: new FormControl<TrustSubType | null>(null, [Validators.required]),
      dateOfEstablishment: new FormControl<Date | null>(null),
      jurisdiction: new FormControl<JurisdictionType | null>(null, [Validators.required]),

      address: AddressFormGroupComponent.defineForm(),
      variationDates: new FormControl<Date[] | null>([]),

      relationships: new FormControl<Relationship[]>([]),
      holdings: new FormControl<SecurityHolding[]>([]),
    }),
  });

  ngOnInit(): void {
    this.resetAddressValidators();
  }

  submit(): void {
    if (this.stepperForm.invalid) {
      this.stepperForm.markAllAsTouched();
      return;
    }

    const jurisdiction = this.trustInformationForm.controls.jurisdiction.value!;

    const trust = new Trust({
      ...this.trustInformationForm.value as Partial<Trust>,
      trustType: getTrustType(this.trustInformationForm.controls.trustSubType.value!),
      jurisdiction,
    });

    this.isLoading = true;
    this.stepperForm.disable();

    this.trustsApiService.addTrust(trust)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.stepperForm.enable();
      }))
      .subscribe({
        next: (res) => this.activeModal.close(res),
        error: (error) => {
          console.warn('[FormAddTrustComponent.submit] Add Trust Error: ', error);
          this.toastr.error('Failed to create Trust');
        }
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;
  }

  async uploadTrustPdfFile(file: File): Promise<void> {
    this.trustInformationForm.disable();
    this.isLoading = true;
    this.trustFromDocument = null;

    const base64File = await toBase64(file);
    const fileTransformation: FileCreateDto = { fileName: file.name, base64FileContent: base64File };

    this.toastr.info(this.analyseTrustPdfMessages.startProcessing);

    this.trustsApiService.analysePdf(fileTransformation)
      .subscribe({
        next: (trust: Trust) => {
          this.trustFromDocument = trust;
          this.isLoading = false;
          this.toastr.success(analyseTrustPdfMessages.successProcessing);
          this.fillTrustInfoForm(trust);
        },
        error: (error) => {
          this.isLoading = false;
          this.trustInformationForm.enable();
          console.warn('[FormAddTrustComponent.uploadFile] Upload File Error: ', error);
          this.toastr.error(analyseTrustPdfMessages.errorProcessing);
        }
      });
  }

  onTrustSelected(trust: Trust): void {
    this.trustFromDocument = null;
    this.fillTrustInfoForm(trust);
  }

  fillTrustInfoForm(trust: Trust): void {
    this.trustInformationForm.enable();
    this.trustInformationForm.patchValue({
      ...trust,
      address: trust.registeredAddress,
    });
  }

  openUploadTrustFileModal(): void {
    const modalRef = this.modalService.open(UploadFilesModalComponent, { size: 'lg' });
    const componentInstance = modalRef.componentInstance as UploadFilesModalComponent;

    componentInstance.config = commonFileUploadConfig;
    componentInstance.multiple = false;
    componentInstance.skipPreview = true;
    componentInstance.getNoteMessage = (config: IFileUploadConfig) => `NOTE: The uploaded file should not exceed ${ config.allowedMaxFileSizeMB }MB`;
    componentInstance.infoMessageHtml = `
        <div>
            The document should be a complete and legible trust deed,
            or related legal document such as a deed of variation, or similar legal document.
        </div>

        <div class="mt-1">
            Key details from the document will be extracted and used to pre-fill
            the form. Once the information appears, please take a moment to review
            and confirm that all fields are complete and accurate before submitting.
        </div>
`;

    componentInstance.save
      .pipe(take(1))
      .subscribe((files: File[]) => this.uploadTrustPdfFile(files[0]));
  }

  private resetAddressValidators(): void {
    Object.values(this.trustInformationForm.controls.address.controls)
      .forEach((control) => control.clearValidators());

    this.trustInformationForm.controls.address.clearValidators();
    this.trustInformationForm.controls.address.updateValueAndValidity();
  }

  get trustInformationForm() {
    return this.stepperForm.controls[AddTrustSteps.TrustInformation];
  }

  defaultMaxDateOrVestingDate = this.maxDate;

  get defaultMinDateOrCommencementDate(): NgbDateStruct {
    return this.trustInformationForm.controls.dateOfEstablishment.value
      ? DatepickerHelper.getStructFromDate(this.trustInformationForm.controls.dateOfEstablishment.value)
      : this.minDate;
  }
}
